import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faMapMarkerAlt, faEdit, faTimes, faCloudUploadAlt, faUsers, faSearch, faFilter, faImage, faEye, faCheck, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Col, Row, Button, Table, Spinner, Badge, Modal, Form, InputGroup } from '@themesberg/react-bootstrap';
import {
  PutApiRequest,
  PostApiRequest,
  DeleteApiRequest,
  resolveImageUrl,
  placeholderImageDataUrl,
} from "../services/api";
import { fetchAllExperiences, setCurrentPage, setFilters, setSelectedGroup, setStateFilter, setImportedOnly, clearError } from '../redux/slices/experiencesSlice';
import { toast } from '../components/Toaster';
import ExperienceLivePreviewModal from '../components/ExperienceLivePreviewModal';

const EXP_THUMB_PLACEHOLDER = placeholderImageDataUrl(60, 60);

const EXPERIENCE_STATES = ['active', 'pending', 'rejected', 'deleted'];

const AllExperiencesPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { experiences, loading, error, pagination, currentPage, filters, selectedGroup, stateFilter, importedOnly, limit, hasLoaded, lastFetchedPage, lastFetchedLimit, lastFetchedFilters, lastFetchedSelectedGroup, lastFetchedStateFilter, lastFetchedImportedOnly } = useSelector(state => state.experiences);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [selectedExperience, setSelectedExperience] = useState(null);

  const listParams = { page: currentPage, limit, filters, selectedGroup, stateFilter, importedOnly };
  const [showFilters, setShowFilters] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLivePreview, setShowLivePreview] = useState(false);
  const [editForm, setEditForm] = useState({
    _id: "",
    title: "",
    description: "",
    images: [],
    category: "",
    price: "FREE",
    grouptype: [],
    location: {
      lat: "",
      lng: "",
      address: ""
    },
    tags: [],
    bookingProvider: "golocal",
    viatorProductCode: "",
    state: "pending",
    experienceHistory: [],
  });
  
  const [validationErrors, setValidationErrors] = useState({});
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = React.useRef(null);
  const [footerStyle, setFooterStyle] = useState({});
  const footerHeight = 80;

  const categories = [
    "Food",
    "Events",
    "Outdoors",
    "Nightlife",
    "Wellness",
    "Art",
    "Hidden Gems",
    "Family",
  ];

  const groupTypes = ["solo", "couple", "friend", "family"];

  const openEditModal = (exp, e) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    setValidationErrors({});
    const rawState = exp.state && EXPERIENCE_STATES.includes(exp.state) ? exp.state : "pending";
    setEditForm({
      _id: exp._id,
      title: exp.title || "",
      description: exp.description || "",
      images: Array.isArray(exp.images) ? exp.images : [],
      category: exp.category || "",
      price: exp.price === "Free" ? "FREE" : (exp.price || "FREE"),
      grouptype: Array.isArray(exp.grouptype) ? exp.grouptype : [],
      location: {
        lat: exp.location?.lat ?? "",
        lng: exp.location?.lng ?? "",
        address: exp.location?.address ?? "",
      },
      tags: Array.isArray(exp.tags) ? exp.tags : [],
      bookingProvider: exp.bookingProvider || "golocal",
      viatorProductCode: exp.viatorProductCode || "",
      state: rawState,
      experienceHistory: Array.isArray(exp.experienceHistory)
        ? exp.experienceHistory.map((h) => ({
            text: h.text,
            createdAt: h.createdAt,
          }))
        : [],
    });
    setShowLivePreview(false);
    setShowEditModal(true);
  };

  useEffect(() => {
    const updateFooterBounds = () => {
      const main = document.querySelector('main.content');
      if (main) {
        const rect = main.getBoundingClientRect();
        setFooterStyle({
          position: 'fixed',
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          bottom: 0,
          background: '#ffffff',
          borderTop: '1px solid #e5e7eb',
          padding: '0.75rem 1rem',
          zIndex: 2000,
        });
      }
    };
    updateFooterBounds();
    window.addEventListener('resize', updateFooterBounds);
    return () => window.removeEventListener('resize', updateFooterBounds);
  }, []);

  const handleGroupTypeToggle = (type) => {
    setEditForm((prev) => ({
      ...prev,
      grouptype: prev.grouptype.includes(type)
        ? prev.grouptype.filter((t) => t !== type)
        : [...prev.grouptype, type],
    }));
  };

  const handlePageChange = (newPage) => {
    dispatch(setCurrentPage(newPage));
  };

  const uploadImages = async (files) => {
    if (!files || files.length === 0) {
      toast.error('No image file provided');
      return;
    }
    
    setUploadingImages(true);
    try {
      const uploadedUrls = [];
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('image', files[i]);
        const response = await PostApiRequest('images/upload', formData);
        const rawUrl = response?.data?.data?.url;
        const imageUrl = typeof rawUrl === 'string' ? rawUrl.replace(/`/g, '').trim() : '';
        if (imageUrl) uploadedUrls.push(imageUrl);
      }
      
      setEditForm(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));
      
      toast.success(`${uploadedUrls.length} image(s) uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error(error?.response?.data?.message || 'Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleImageUpload = (e) => {
    uploadImages(e.target.files);
  };

  const handleRemoveImage = (index) => {
    setEditForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const validateEditForm = () => {
    const errors = {};
    if (!editForm.title.trim()) errors.title = 'Title is required';
    if (!editForm.description.trim()) errors.description = 'Description is required';
    if (!editForm.category) errors.category = 'Category is required';
    if (editForm.bookingProvider === 'viator' && (!editForm.viatorProductCode || !editForm.viatorProductCode.trim())) {
      errors.viatorProductCode = 'Viator Product Code is required when Booking Provider is Viator';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateExperience = async () => {
    if (!validateEditForm()) {
      toast.error('Please fix validation errors');
      return;
    }
    
    setActionLoading(true);
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description,
        images: editForm.images,
        category: editForm.category,
        price: editForm.price,
        grouptype: editForm.grouptype,
        location: editForm.location || {},
        tags: editForm.tags,
        bookingProvider: editForm.bookingProvider || "golocal",
        ...(editForm.bookingProvider === "viator" && {
          viatorProductCode: editForm.viatorProductCode,
        }),
        ...(editForm.bookingProvider !== "viator" && {
          viatorProductCode: null,
        }),
        state: editForm.state,
      };
      
      await PutApiRequest(`experiences/${editForm._id}`, payload);
      toast.success('Experience updated successfully!');
      setShowLivePreview(false);
      setShowEditModal(false);
      // Refetch experiences after update
      dispatch(fetchAllExperiences(listParams));
    } catch (error) {
      console.error('Error updating experience:', error);
      toast.error(error?.response?.data?.message || 'Failed to update experience');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get('state');
    if (s && ['all', 'active', 'pending', 'rejected', 'deleted'].includes(s)) {
      dispatch(setStateFilter(s));
    }
    if (params.get('importedOnly') === '1') {
      dispatch(setImportedOnly(true));
    }
  }, [location.search, dispatch]);

  useEffect(() => {
    dispatch(fetchAllExperiences(listParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const shouldRefetch = hasLoaded && (
      currentPage !== lastFetchedPage ||
      limit !== lastFetchedLimit ||
      selectedGroup !== lastFetchedSelectedGroup ||
      stateFilter !== lastFetchedStateFilter ||
      importedOnly !== lastFetchedImportedOnly ||
      JSON.stringify(filters) !== JSON.stringify(lastFetchedFilters)
    );
    if (shouldRefetch) {
      dispatch(fetchAllExperiences(listParams));
    }
  }, [currentPage, filters, selectedGroup, stateFilter, importedOnly, hasLoaded, dispatch, limit, lastFetchedPage, lastFetchedLimit, lastFetchedFilters, lastFetchedSelectedGroup, lastFetchedStateFilter, lastFetchedImportedOnly]);

  // Show error toast if loading fails
  useEffect(() => {
    if (error && hasLoaded) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch, hasLoaded]);

  const handleDelete = async () => {
    if (!selectedExperience) return;
    setActionLoading(true);
    try {
      const qs = deleteReason.trim()
        ? `?reason=${encodeURIComponent(deleteReason.trim())}`
        : "";
      await DeleteApiRequest(`admin/experiences/${selectedExperience._id}${qs}`);
      toast.success('Experience removed from public view');
      setShowDeleteModal(false);
      setSelectedExperience(null);
      setDeleteReason('');
      dispatch(fetchAllExperiences(listParams));
    } catch (error) {
      console.error('Error deleting experience:', error);
      toast.error(error?.response?.data?.message || 'Failed to remove experience');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveExperience = async (expId, e) => {
    e.stopPropagation();
    setActionLoading(true);
    try {
      await PutApiRequest(`admin/experiences/${expId}/approve`, {});
      toast.success('Experience approved successfully!');
      dispatch(fetchAllExperiences(listParams));
    } catch (error) {
      console.error('Error approving experience:', error);
      toast.error(error?.response?.data?.message || 'Failed to approve experience');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!selectedExperience) return;
    setActionLoading(true);
    try {
      await PutApiRequest(`admin/experiences/${selectedExperience._id}/reject`, {
        reason: rejectReason.trim() || undefined,
      });
      toast.success('Experience rejected');
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedExperience(null);
      dispatch(fetchAllExperiences(listParams));
    } catch (error) {
      console.error('Error rejecting experience:', error);
      toast.error(error?.response?.data?.message || 'Failed to reject experience');
    } finally {
      setActionLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Food": "danger",
      "Events": "primary",
      "Outdoors": "success",
      "Nightlife": "warning",
      "Wellness": "info",
      "Art": "secondary",
      "Hidden Gems": "dark",
      "Family": "primary",
    };
    return colors[category] || "secondary";
  };

  const experienceStateVariant = (s) => {
    if (s === 'active') return 'success';
    if (s === 'pending') return 'warning';
    if (s === 'rejected') return 'danger';
    if (s === 'deleted') return 'secondary';
    return 'secondary';
  };

  return (
    <>
      <style>{`
        .page-header {
          background: #ffffff;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          border-left: 4px solid #4f46e5;
        }
        
        .page-header h2 {
          font-size: 2rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }
        
        .page-header p {
          color: #64748b;
          font-size: 1rem;
          margin-bottom: 0;
        }
        
        .stats-badge {
          background: #f1f5f9;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: #4f46e5;
          margin-top: 1rem;
        }
        
        .filter-card {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 1.5rem;
        }
        
        .filter-toggle-btn {
          background: #4f46e5;
          border: none;
          border-radius: 8px;
          padding: 0.6rem 1.25rem;
          font-weight: 600;
          transition: all 0.3s ease;
          color: white;
        }
        
        .filter-toggle-btn:hover {
          background: #4338ca;
          transform: translateY(-1px);
        }
        
        .search-input {
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          padding: 0.6rem 1rem;
          transition: all 0.3s ease;
        }
        
        .search-input:focus {
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }
        
        .filter-label {
          font-weight: 600;
          color: #475569;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }
        
        .group-type-selector {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        
        .group-type-btn {
          flex: 1;
          min-width: 100px;
          padding: 0.6rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.3s ease;
          font-weight: 600;
          text-transform: capitalize;
          text-align: center;
        }
        
        .group-type-btn:hover {
          border-color: #4f46e5;
          background: #f8fafc;
        }
        
        .group-type-btn.active {
          background: #4f46e5;
          color: white;
          border-color: #4f46e5;
        }
        
        .group-type-checkbox {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        
        .group-checkbox-item {
          flex: 1;
          min-width: 120px;
          padding: 0.75rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .group-checkbox-item:hover {
          border-color: #4f46e5;
          background: #f8fafc;
        }
        
        .group-checkbox-item.selected {
          background: #eff6ff;
          border-color: #4f46e5;
        }
        
        .group-checkbox-item input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
        
        .experiences-table {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        
        .experiences-table table {
          margin-bottom: 0;
        }
        
        .experiences-table thead {
          background: #f8fafc;
        }
        
        .experiences-table th {
          font-weight: 700;
          color: #1e293b;
          padding: 1rem;
          border-bottom: 2px solid #e2e8f0;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .experiences-table tbody tr {
          cursor: pointer;
          transition: all 0.2s ease;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .experiences-table tbody tr:hover {
          background: #f8fafc;
        }
        
        .experiences-table td {
          padding: 1rem;
          vertical-align: middle;
          color: #475569;
        }
        
        .experience-image {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          object-fit: cover;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        .experience-title {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
          font-size: 0.95rem;
        }
        
        .experience-creator {
          color: #94a3b8;
          font-size: 0.8rem;
        }
        
        .action-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: none;
          transition: all 0.3s ease;
          margin: 0 0.25rem;
        }
        
        .action-btn.edit {
          background: #4f46e5;
          color: white;
        }
        
        .action-btn.edit:hover {
          background: #4338ca;
          transform: scale(1.05);
        }
        
        .action-btn.delete {
          background: #ef4444;
          color: white;
        }
        
        .action-btn.delete:hover {
          background: #dc2626;
          transform: scale(1.05);
        }
        
        .action-btn.approve {
          background: #10b981;
          color: white;
        }
        
        .action-btn.approve:hover {
          background: #059669;
          transform: scale(1.05);
        }

        .action-btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
          transform: none;
        }
        
        .pagination-container {
          background: white;
          border-radius: 12px;
          padding: 1.25rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-top: 1.5rem;
        }
        
        .pagination-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }
        
        .page-btn {
          border-radius: 8px;
          padding: 0.6rem 1.25rem;
          font-weight: 600;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          transition: all 0.3s ease;
        }
        
        .page-btn:hover:not(:disabled) {
          background: #4f46e5;
          color: white;
          border-color: #4f46e5;
        }
        
        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        
        .empty-state svg {
          font-size: 3rem;
          color: #cbd5e0;
          margin-bottom: 1rem;
        }
        
        .empty-state h3 {
          color: #475569;
          margin-bottom: 0.5rem;
        }
        
        .empty-state p {
          color: #94a3b8;
        }
        
        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 4rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        
        .modal-content {
          border-radius: 12px;
          border: none;
        }
        
        .modal-header {
          background: #4f46e5;
          color: white;
          border-radius: 12px 12px 0 0;
          padding: 1.25rem 1.5rem;
        }

        .modal-title {
          color: white;
        }
        
        .modal-title {
          font-weight: 700;
        }
        
        .modal-header .btn-close {
          filter: brightness(0) invert(1);
        }

        .modal-header .modal-header-action {
          color: white;
          border-color: white;
          background: transparent;
        }

        .modal-header .modal-header-action:hover,
        .modal-header .modal-header-action:focus,
        .modal-header .modal-header-action:active {
          color: #4f46e5;
          border-color: white;
          background: white;
        }
        
        .image-upload-zone {
          border: 2px dashed #cbd5e0;
          border-radius: 12px;
          padding: 2rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          background: #f8fafc;
        }
        
        .image-upload-zone:hover {
          border-color: #4f46e5;
          background: #f1f5f9;
        }
        
        .image-preview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }
        
        .image-preview-item {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          aspect-ratio: 1;
        }
        
        .image-preview-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .image-remove-btn {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: rgba(239, 68, 68, 0.95);
          color: white;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .image-remove-btn:hover {
          background: #dc2626;
          transform: scale(1.1);
        }
        
        @media (max-width: 768px) {
          .group-type-btn, .group-checkbox-item {
            flex: 1 1 calc(50% - 0.375rem);
          }
          
          .experiences-table {
            overflow-x: auto;
          }
        }
      `}</style>

      <div className="page-header">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <h2>Experiences</h2>
            <p>Full list — filter by lifecycle state (and imported places).</p>
            <div className="stats-badge">
              <FontAwesomeIcon icon={faUsers} />
              {pagination?.total || 0} in this view
            </div>
          </div>
          <Link to="/experiences/create" className="btn btn-primary" style={{ borderRadius: '8px', fontWeight: 600 }}>
            Create experience
          </Link>
        </div>
        <Row className="g-2 mt-3 align-items-end">
          <Col xs={12} sm={6} md={4}>
            <div className="filter-label">State</div>
            <Form.Select
              value={stateFilter}
              onChange={(e) => dispatch(setStateFilter(e.target.value))}
              style={{ borderRadius: '8px' }}
            >
              <option value="all">All states</option>
              <option value="active">active</option>
              <option value="pending">pending</option>
              <option value="rejected">rejected</option>
              <option value="deleted">deleted</option>
            </Form.Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Check
              type="checkbox"
              id="imported-only"
              label="Imported places only"
              checked={importedOnly}
              onChange={(e) => dispatch(setImportedOnly(e.target.checked))}
              style={{ fontWeight: 600, color: '#475569', paddingTop: '1.5rem' }}
            />
          </Col>
        </Row>
      </div>
 
      <div className="filter-card">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0" style={{ fontWeight: 700, color: '#1e293b' }}>
            <FontAwesomeIcon icon={faSearch} className="me-2" />
            Search & Filters
          </h5>
          <Button 
            className="filter-toggle-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            {showFilters ? 'Hide' : 'Show'} Filters
          </Button>
        </div>

        <Row className="mb-3">
          <Col md={12}>
            <InputGroup>
              <InputGroup.Text style={{ 
                background: 'white', 
                border: '1px solid #e2e8f0',
                borderRight: 'none',
                borderRadius: '8px 0 0 8px'
              }}>
                <FontAwesomeIcon icon={faSearch} style={{ color: '#4f46e5' }} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search experiences by title, description, tags..."
                value={filters.search}
                onChange={(e) => {
                  dispatch(setFilters({ ...filters, search: e.target.value }));
                  dispatch(setCurrentPage(1));
                }}
                className="search-input"
                style={{ borderLeft: 'none', borderRadius: '0 8px 8px 0' }}
              />
            </InputGroup>
          </Col>
        </Row>

        {showFilters && (
          <>
            <Row className="mb-3">
              <Col md={3}>
                <div className="filter-label">Category</div>
                <Form.Select
                  value={filters.category}
                  onChange={(e) => {
                    dispatch(setFilters({ ...filters, category: e.target.value }));
                    dispatch(setCurrentPage(1));
                  }}
                  className="search-input"
                >
                  <option value="">All Categories</option>
                  {categories.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <div className="filter-label">Price Range</div>
                <Form.Select
                  value={filters.price}
                  onChange={(e) => {
                    dispatch(setFilters({ ...filters, price: e.target.value }));
                    dispatch(setCurrentPage(1));
                  }}
                  className="search-input"
                >
                  <option value="">All Prices</option>
                  <option value="FREE">Free</option>
                  <option value="$">$</option>
                  <option value="$$">$$</option>
                  <option value="$$$">$$$</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <div className="filter-label">Date</div>
                <Form.Control
                  type="date"
                  value={filters.date}
                  onChange={(e) => {
                    dispatch(setFilters({ ...filters, date: e.target.value }));
                    dispatch(setCurrentPage(1));
                  }}
                  className="search-input"
                />
              </Col>
              <Col md={3}>
                <div className="filter-label">Day of Week</div>
                <Form.Select
                  value={filters.day}
                  onChange={(e) => {
                    dispatch(setFilters({ ...filters, day: e.target.value }));
                    dispatch(setCurrentPage(1));
                  }}
                  className="search-input"
                >
                  <option value="">All Days</option>
                  {["monday","tuesday","wednesday","thursday","friday","saturday","sunday"].map(d => (
                    <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <div className="filter-label">Group Type (Single Selection)</div>
                <div className="group-type-selector">
                  {groupTypes.map((type) => (
                    <div
                      key={type}
                      className={`group-type-btn ${selectedGroup === type ? 'active' : ''}`}
                      onClick={() => {
                        dispatch(setSelectedGroup(selectedGroup === type ? "" : type));
                        dispatch(setCurrentPage(1));
                      }}
                    >
                      <FontAwesomeIcon icon={faUsers} className="me-2" />
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </div>
                  ))}
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Check
                  type="checkbox"
                  label="Upcoming Events Only"
                  checked={filters.upcoming}
                  onChange={(e) => {
                    dispatch(setFilters({ ...filters, upcoming: e.target.checked }));
                    dispatch(setCurrentPage(1));
                  }}
                  style={{ fontWeight: 600, color: '#475569' }}
                />
              </Col>
              <Col md={4}>
                <Form.Check
                  type="checkbox"
                  label="This Weekend"
                  checked={filters.thisWeekend}
                  onChange={(e) => {
                    dispatch(setFilters({ ...filters, thisWeekend: e.target.checked }));
                    dispatch(setCurrentPage(1));
                  }}
                  style={{ fontWeight: 600, color: '#475569' }}
                />
              </Col>
            </Row>

            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  dispatch(setFilters({
                    category: "",
                    price: "",
                    search: "",
                    lat: "",
                    lng: "",
                    distance: "",
                    date: "",
                    day: "",
                    time: "",
                    upcoming: false,
                    thisWeekend: false
                  }));
                  dispatch(setSelectedGroup(""));
                  dispatch(setStateFilter('all'));
                  dispatch(setImportedOnly(false));
                  dispatch(setCurrentPage(1));
                }}
                style={{ borderRadius: '8px', fontWeight: 600 }}
              >
                Clear All
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Initial load - show nothing while fetching */}
      {loading && !hasLoaded ? (
        <div className="loading-container" style={{ minHeight: '300px' }}>
          {/* Silent loading on first mount */}
        </div>
      ) : loading && hasLoaded ? (
        <div className="loading-container">
          <Spinner animation="border" style={{ color: '#4f46e5', width: '3rem', height: '3rem' }} />
        </div>
      ) : experiences.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faSearch} />
          <h3>No Experiences Found</h3>
          <p>Try adjusting your filters or search criteria</p>
        </div>
      ) : (
        <>
          <div className="experiences-table" style={{ marginBottom: footerHeight }}>
            <Table responsive hover>
              <thead>
                <tr>
                  <th style={{ width: '160px' }} className="text-center">Actions</th>
                  <th style={{ width: '80px' }}>Image</th>
                  <th>Title & Creator</th>
                  <th style={{ width: '100px' }}>State</th>
                  <th style={{ width: '120px' }}>Category</th>
                  <th style={{ width: '200px' }}>Location</th>
                  <th style={{ width: '100px' }}>Price</th>
                  <th style={{ width: '110px' }}>Provider</th>
                  <th style={{ width: '150px' }}>Group Type</th>
                  <th style={{ width: '120px' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {experiences.map(exp => {
                  const rawImage =
                    exp.fileAssets?.[0]?.urls?.medium ||
                    exp.fileAssets?.[0]?.urls?.high ||
                    exp.fileAssets?.[0]?.urls?.original ||
                    exp.images?.[0];
                  const firstImageUrl = resolveImageUrl(rawImage);
                  const st = exp.state || '';
                  const canApprove = st !== 'active';
                  const canReject = st !== 'rejected' && st !== 'deleted';
                  const canDelete = st !== 'deleted';
                  return (
                  <tr
                    key={exp._id}
                    onClick={() => openEditModal(exp)}
                    style={{ cursor: "pointer" }}
                    title="Edit experience"
                  >
                    <td className="text-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="action-btn approve"
                        title={canApprove ? 'Approve (set active)' : 'Already active'}
                        onClick={(e) => handleApproveExperience(exp._id, e)}
                        disabled={actionLoading || !canApprove}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                      <button
                        type="button"
                        className="action-btn reject"
                        title={
                          canReject
                            ? 'Reject'
                            : st === 'rejected'
                              ? 'Already rejected'
                              : 'Cannot reject a deleted experience'
                        }
                        style={{ background: '#f59e0b', color: 'white' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedExperience(exp);
                          setRejectReason('');
                          setShowRejectModal(true);
                        }}
                        disabled={actionLoading || !canReject}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                      <button
                        type="button"
                        className="action-btn delete"
                        title={canDelete ? 'Mark as deleted (hidden)' : 'Already deleted'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedExperience(exp);
                          setDeleteReason('');
                          setShowDeleteModal(true);
                        }}
                        disabled={actionLoading || !canDelete}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                    <td>
                      <img
                        src={firstImageUrl || EXP_THUMB_PLACEHOLDER}
                        alt={exp.title}
                        className="experience-image"
                        onError={(e) => { e.target.src = EXP_THUMB_PLACEHOLDER; }}
                      />
                    </td>
                    <td>
                      <div className="experience-title">
                        {exp.title}
                        {exp.importSource && (
                          <Badge bg="info" className="ms-2" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}>
                            {exp.importSource}
                          </Badge>
                        )}
                      </div>
                      <div className="experience-creator">by {exp.createdBy?.name || 'Unknown'}</div>
                    </td>
                    <td>
                      <Badge bg={experienceStateVariant(exp.state)} text={exp.state === 'pending' ? 'dark' : undefined} style={{ fontSize: '0.75rem', textTransform: 'lowercase' }}>
                        {exp.state || '—'}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={getCategoryColor(exp.category)} style={{ fontSize: '0.8rem', padding: '0.4rem 0.75rem' }}>
                        {exp.category || 'N/A'}
                      </Badge>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.85rem' }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" style={{ color: '#94a3b8' }} />
                        {exp.location?.address || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        {exp.price || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <Badge
                        bg={exp.bookingProvider === 'viator' ? 'info' : 'success'}
                        style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem', textTransform: 'capitalize' }}
                      >
                        {exp.bookingProvider === 'viator' ? 'Viator' : 'GoLocal'}
                      </Badge>
                    </td>
                    <td>
                      {exp.grouptype && exp.grouptype.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {exp.grouptype.map((type, idx) => (
                            <Badge key={idx} bg="secondary" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}>
                              {type}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize: '0.85rem' }}>
                        <FontAwesomeIcon icon={faCalendar} className="me-1" style={{ color: '#94a3b8' }} />
                        {exp.datetime ? new Date(exp.datetime).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {experiences.length > 0 && (
            <div style={footerStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: '#475569' }}>
                  Page {currentPage} of {pagination.pages || 1} ({pagination.total || 0} total experiences)
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <Button
                    className="page-btn"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                    let end = Math.min(pagination.pages || 1, start + maxVisible - 1);
                    if (end - start < maxVisible - 1) {
                      start = Math.max(1, end - maxVisible + 1);
                    }
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <Button
                          key={i}
                          className="page-btn"
                          style={i === currentPage ? { background: '#4f46e5', color: 'white', borderColor: '#4f46e5' } : {}}
                          onClick={() => handlePageChange(i)}
                        >
                          {i}
                        </Button>
                      );
                    }
                    return pages;
                  })()}
                  <Button
                    className="page-btn"
                    disabled={currentPage >= (pagination.pages || 1)}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
          {experiences.length > 0 && (
            <div style={{ height: `${footerHeight}px` }} />
          )}
        </>
      )}

      <Modal show={showRejectModal} onHide={() => !actionLoading && setShowRejectModal(false)} centered>
        <Modal.Header closeButton={!actionLoading}>
          <Modal.Title>Reject experience</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-2">Sets state to <strong>rejected</strong>. The record stays in the database.</p>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => { setShowRejectModal(false); setSelectedExperience(null); }} disabled={actionLoading}>Cancel</Button>
          <Button variant="warning" onClick={handleRejectSubmit} disabled={actionLoading}>
            {actionLoading ? 'Saving…' : 'Reject'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => !actionLoading && setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton={!actionLoading}>
          <Modal.Title>Remove experience from public view</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '2rem' }}>
          {selectedExperience && (
            <>
              <div style={{
                background: '#fffbeb',
                padding: '1.25rem',
                borderRadius: '8px',
                marginBottom: '1.25rem',
                border: '1px solid #fde68a'
              }}>
                <h6 style={{ color: '#92400e', marginBottom: '0.75rem' }}>
                  Hide this experience in the app
                </h6>
                <p style={{ color: '#78350f', marginBottom: 0, fontSize: '0.9rem' }}>
                  Sets state to <strong>deleted</strong>. The record stays in the database; you can restore visibility from the edit screen or set state to active again.
                </p>
              </div>

              <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px' }}>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Title:</strong> {selectedExperience.title}
                </p>
                <p style={{ marginBottom: 0 }}>
                  <strong>Creator:</strong> {selectedExperience.createdBy?.name || 'Unknown'}
                </p>
              </div>

              <Form.Group className="mt-3">
                <Form.Label style={{ fontWeight: 600 }}>Reason (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Why is this being removed?"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                />
                <Form.Text className="text-muted">
                  This will be appended to the experience history.
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ border: 'none', padding: '1.25rem' }}>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedExperience(null);
              setDeleteReason('');
            }}
            disabled={actionLoading}
            style={{ borderRadius: '8px', fontWeight: 600, padding: '0.6rem 1.25rem' }}
          >
            Cancel
          </Button>
          <Button
            style={{
              background: '#ef4444',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              padding: '0.6rem 1.25rem'
            }}
            onClick={handleDelete}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Removing…
              </>
            ) : (
              'Remove from public view'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Experience Modal */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowLivePreview(false);
          setShowEditModal(false);
        }}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton className="align-items-center">
          <Modal.Title className="flex-grow-1 mb-0">Edit Experience</Modal.Title>
          <Button
            variant="outline-primary"
            size="sm"
            className="me-2 modal-header-action"
            type="button"
            onClick={() => setShowLivePreview(true)}
          >
            <FontAwesomeIcon icon={faEye} className="me-2" />
            Preview live
          </Button>
        </Modal.Header>
        <Modal.Body style={{ padding: '2rem' }}>
          {Object.keys(validationErrors).length > 0 && (
            <div style={{ 
              background: '#fef2f2', 
              padding: '1rem', 
              borderRadius: '8px', 
              marginBottom: '1.5rem',
              border: '1px solid #fecaca'
            }}>
              <strong style={{ color: '#991b1b' }}>Please fix the following errors:</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                {Object.values(validationErrors).map((e, i) => (
                  <li key={i} style={{ color: '#7f1d1d' }}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ marginBottom: '2rem' }}>
            <h5 style={{ marginBottom: '1rem', fontWeight: 700, color: '#1e293b' }}>Basic Information</h5>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>State</Form.Label>
              <Form.Select
                value={editForm.state}
                onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                className="search-input"
              >
                {EXPERIENCE_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Controls visibility in the app (active = public when approved).
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>Title *</Form.Label>
              <Form.Control
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                isInvalid={validationErrors.title}
                className="search-input"
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.title}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                isInvalid={validationErrors.description}
                className="search-input"
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.description}
              </Form.Control.Feedback>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600 }}>Category *</Form.Label>
                  <Form.Select
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                    isInvalid={validationErrors.category}
                    className="search-input"
                  >
                    <option value="">Select a category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.category}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600 }}>Price</Form.Label>
                  <Form.Select
                    value={editForm.price}
                    onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                    className="search-input"
                  >
                    <option value="FREE">Free</option>
                    <option value="$">$</option>
                    <option value="$$">$$</option>
                    <option value="$$$">$$$</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <h5 style={{ marginBottom: '1rem', fontWeight: 700, color: '#1e293b' }}>
              <FontAwesomeIcon icon={faImage} className="me-2" />
              Images
            </h5>
            
            <div
              className="image-upload-zone"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                uploadImages(e.dataTransfer.files);
              }}
            >
              <FontAwesomeIcon icon={faCloudUploadAlt} style={{ fontSize: '2.5rem', color: '#cbd5e0', marginBottom: '0.75rem' }} />
              <div style={{ fontWeight: 600, color: '#475569', marginBottom: '0.5rem' }}>
                Click to upload or drag and drop
              </div>
              <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                PNG, JPG up to 5MB each
              </div>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />

            {uploadingImages && (
              <div style={{ textAlign: 'center', marginTop: '1rem', color: '#4f46e5' }}>
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading images...
              </div>
            )}

            {editForm.images.length > 0 && (
              <div className="image-preview-grid">
                {editForm.images.map((img, idx) => (
                  <div key={idx} className="image-preview-item">
                    <img
                      src={resolveImageUrl(img)}
                      alt={`Preview ${idx + 1}`}
                      onError={(e) => {
                        e.target.src = EXP_THUMB_PLACEHOLDER;
                      }}
                    />
                    <button
                      className="image-remove-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveImage(idx);
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <h5 style={{ marginBottom: '1rem', fontWeight: 700, color: '#1e293b' }}>
              <FontAwesomeIcon icon={faUsers} className="me-2" />
              Suitable For (Multiple Selection)
            </h5>
            
            <div className="group-type-checkbox">
              {['solo', 'couple', 'friend', 'family'].map(type => (
                <div
                  key={type}
                  className={`group-checkbox-item ${editForm.grouptype.includes(type) ? 'selected' : ''}`}
                  onClick={() => handleGroupTypeToggle(type)}
                >
                  <input
                    type="checkbox"
                    checked={editForm.grouptype.includes(type)}
                    onChange={() => {}}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{type}</span>
                </div>
              ))}
            </div>
          </div>

          <Form.Group>
            <Form.Label style={{ fontWeight: 600 }}>Tags</Form.Label>
            <Form.Control
              type="text"
              value={editForm.tags.join(', ')}
              onChange={(e) => setEditForm({ 
                ...editForm, 
                tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) 
              })}
              placeholder="e.g., food, adventure, cultural"
              className="search-input"
            />
            <Form.Text className="text-muted">Separate tags with commas</Form.Text>
          </Form.Group>

          <div style={{ marginTop: '2rem' }}>
            <h5 style={{ marginBottom: '1rem', fontWeight: 700, color: '#1e293b' }}>Booking Provider</h5>
            
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>Booking Provider</Form.Label>
              <Form.Select
                value={editForm.bookingProvider}
                onChange={(e) => setEditForm({
                  ...editForm,
                  bookingProvider: e.target.value,
                  ...(e.target.value !== 'viator' && { viatorProductCode: '' }),
                })}
                className="search-input"
              >
                <option value="golocal">GoLocal</option>
                <option value="viator">Viator</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Select "Viator" to link this experience to a Viator product for affiliate booking.
              </Form.Text>
            </Form.Group>

            {editForm.bookingProvider === 'viator' && (
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600 }}>Viator Product Code *</Form.Label>
                <Form.Control
                  type="text"
                  value={editForm.viatorProductCode}
                  onChange={(e) => setEditForm({ ...editForm, viatorProductCode: e.target.value })}
                  placeholder="e.g. 12345P1"
                  isInvalid={!!validationErrors.viatorProductCode}
                  className="search-input"
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.viatorProductCode}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Enter the Viator product code to generate an affiliate booking link.
                </Form.Text>
              </Form.Group>
            )}
          </div>

          <div style={{ marginTop: '2rem' }}>
            <h5 style={{ marginBottom: '0.75rem', fontWeight: 700, color: '#1e293b' }}>History</h5>
            <p className="text-muted small mb-2">
              Newest first. Rejection reasons and other changes are recorded here.
            </p>
            {Array.isArray(editForm.experienceHistory) && editForm.experienceHistory.length > 0 ? (
              <div
                className="p-3 rounded"
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  maxHeight: '260px',
                  overflowY: 'auto',
                }}
              >
                <ul className="list-unstyled mb-0" style={{ fontSize: '0.875rem' }}>
                  {[...editForm.experienceHistory]
                    .sort(
                      (a, b) =>
                        new Date(b.createdAt || 0).getTime() -
                        new Date(a.createdAt || 0).getTime()
                    )
                    .map((entry, idx, arr) => (
                      <li
                        key={`${entry.createdAt}-${idx}`}
                        className="mb-2 pb-2"
                        style={{
                          borderBottom: idx < arr.length - 1 ? '1px solid #e2e8f0' : 'none',
                        }}
                      >
                        <div style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                          {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : '—'}
                        </div>
                        <div style={{ color: '#334155', whiteSpace: 'pre-wrap' }}>{entry.text}</div>
                      </li>
                    ))}
                </ul>
              </div>
            ) : (
              <div className="p-3 rounded" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <span className="text-muted small">No history yet.</span>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ border: 'none', padding: '1.25rem' }}>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowLivePreview(false);
              setShowEditModal(false);
            }}
            disabled={actionLoading || uploadingImages}
            style={{ borderRadius: '8px', fontWeight: 600, padding: '0.6rem 1.25rem' }}
          >
            Cancel
          </Button>
          <Button
            variant="outline-primary"
            type="button"
            onClick={() => setShowLivePreview(true)}
            disabled={actionLoading || uploadingImages}
            style={{ borderRadius: '8px', fontWeight: 600, padding: '0.6rem 1.25rem' }}
          >
            <FontAwesomeIcon icon={faEye} className="me-2" />
            Preview live
          </Button>
          <Button
            style={{ 
              background: '#4f46e5',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              padding: '0.6rem 1.25rem'
            }}
            onClick={handleUpdateExperience}
            disabled={actionLoading || uploadingImages}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faEdit} className="me-2" />
                Save Changes 
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ExperienceLivePreviewModal
        show={showLivePreview}
        onHide={() => setShowLivePreview(false)}
        experience={editForm}
      />
    </>
  );
};

export default AllExperiencesPage;